import { Badge, Card, Table, TableBody, TableContainer, TableHead, TableHeader, TableRow } from '@ksyos/design-system';
import { isEmpty } from 'lodash';
import { Fragment, useState } from 'react';
import { ActiveOrInactive } from '@common/enums';
import type {
    ICompensationPayoutAgreements,
    IEmploymentCarePaths,
    IServiceGroupSettingWithServiceGroup,
} from './PersonalSettingsCarePathsOverview';
import { translateKey } from '../../../../translate';
import { PersonalSettingsCarePathOverViewDetailsHeader } from './PersonalSettingsCarePathOverViewDetailsHeader';
import { PersonalSettingsCarePathOverviewDetailsInactiveCareGroupFoldOut } from './PersonalSettingsCarePathOverviewDetailsInactiveCareGroupFoldOut';
import { PersonalSettingsCarePathOverviewDetailsInactiveServiceGroupInActiveCarePathBlock } from './PersonalSettingsCarePathOverviewDetailsInactiveServiceGroupInActiveCarePathBlock';
import { PersonalSettingsCarePathOverviewDetailsServiceGroupRow } from './PersonalSettingsCarePathOverviewDetailsServiceGroupRow';

interface IPersonalSettingsCarePathOverviewDetails {
    activeCarePaths: IEmploymentCarePaths;
    compensationPayoutAgreements?: ICompensationPayoutAgreements;
    groupSettings: IServiceGroupSettingWithServiceGroup[];
    inactiveCarePaths: IEmploymentCarePaths;
}

export function PersonalSettingsCarePathOverviewDetails(props: IPersonalSettingsCarePathOverviewDetails) {
    const [showInactiveCareGroups, setShowInactiveCareGroups] = useState(false);

    if (isEmpty(props.activeCarePaths) && isEmpty(props.inactiveCarePaths)) {
        return null;
    }

    return (
        <Card>
            <Card.Header title={translateKey('personalSettings.carePathDetails.carePathDetails')} />
            <TableContainer>
                <Table>
                    <TableHead>
                        <TableRow>
                            {[
                                { name: 'carePath' },
                                { name: 'rights' },
                                { name: 'activeSince' },
                                { name: 'careGroup' },
                            ].map((header) => (
                                <TableHeader key={header.name}>
                                    {translateKey(`personalSettings.carePathDetails.${header.name}`)}
                                </TableHeader>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {!isEmpty(props.activeCarePaths) && getCarePathBlock(true)}
                        {!isEmpty(props.inactiveCarePaths) && showInactiveCareGroups && getCarePathBlock(false)}

                        <PersonalSettingsCarePathOverviewDetailsInactiveCareGroupFoldOut
                            inactiveCarePaths={props.inactiveCarePaths}
                            showInactiveCareGroups={showInactiveCareGroups}
                            toggleShowInactiveCarePaths={toggleShowInactiveCarePaths}
                        />
                    </TableBody>
                </Table>
            </TableContainer>
        </Card>
    );

    function getCarePathBlock(isActiveCarePath: boolean) {
        const carePaths = isActiveCarePath ? props.activeCarePaths : props.inactiveCarePaths;
        const serviceGroupType = isActiveCarePath ? ActiveOrInactive.active : ActiveOrInactive.inactive;

        return Object.keys(carePaths).map((carePathName: string, carePathIndex: number) => {
            return (
                <Fragment key={`${carePathName}-${carePathIndex}-${serviceGroupType}`}>
                    <PersonalSettingsCarePathOverViewDetailsHeader
                        carePathName={carePathName}
                        inactiveFlag={inactiveFlag(!isActiveCarePath)}
                    />
                    {getServiceGroupBlock(carePaths, carePathName, isActiveCarePath, serviceGroupType)}
                    <PersonalSettingsCarePathOverviewDetailsInactiveServiceGroupInActiveCarePathBlock
                        carePathName={carePathName}
                        carePaths={carePaths}
                        inactiveServiceGroupBlock={getServiceGroupBlock(
                            carePaths,
                            carePathName,
                            isActiveCarePath,
                            ActiveOrInactive.inactive
                        )}
                        isActiveCarePath={isActiveCarePath}
                    />
                </Fragment>
            );
        });
    }

    function getServiceGroupBlock(
        carePaths: IEmploymentCarePaths,
        carePathName: string,
        isActiveCarePath: boolean,
        serviceGroupType: ActiveOrInactive
    ) {
        const inactiveServiceGroupInActiveCarePath = isActiveCarePath && serviceGroupType === ActiveOrInactive.inactive;

        return Object.keys(carePaths[carePathName][serviceGroupType]).map(
            (serviceGroupName, serviceGroupNameIndex, serviceGroupNameArray) => {
                return (
                    <PersonalSettingsCarePathOverviewDetailsServiceGroupRow
                        carePathName={carePathName}
                        carePaths={carePaths}
                        compensationPayoutAgreements={props.compensationPayoutAgreements}
                        groupSettings={props.groupSettings}
                        inactiveFlag={inactiveFlag}
                        inactiveServiceGroupInActiveCarePath={inactiveServiceGroupInActiveCarePath}
                        isActiveCarePath={isActiveCarePath}
                        key={`${carePathName}-${serviceGroupType}-${serviceGroupName}-${serviceGroupNameIndex}`}
                        serviceGroupName={serviceGroupName}
                        serviceGroupNameArray={serviceGroupNameArray}
                        serviceGroupNameIndex={serviceGroupNameIndex}
                        serviceGroupType={serviceGroupType}
                    />
                );
            }
        );
    }

    function toggleShowInactiveCarePaths() {
        setShowInactiveCareGroups(!showInactiveCareGroups);
    }

    function inactiveFlag(notActive: boolean) {
        if (notActive) {
            return (
                <Badge
                    size="small"
                    text={translateKey('common.isInactive')}
                />
            );
        }
    }
}
